import { useState } from 'react';
import { Plus, Pencil, Trash2, Search, BarChart3, FileText, CheckCircle, Clock, XCircle, Award, DollarSign, Download } from 'lucide-react';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import { useFines, useCreateFine, useUpdateFine, useDeleteFine, useExportFines, usePayFine } from '../hooks/useFines.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useDebounce } from '../hooks/useDebounce.ts';
import Select from '../components/ui/Select.tsx';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const statusOptions = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Paid' },
  { value: 'waived', label: 'Waived' },
];

export default function FinesPage() {
  const { can } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFinePaymentModal, setShowFinePaymentModal] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 500);

  const { data: finesData, isLoading } = useFines({
    search: debouncedSearch,
  });
  const createFine = useCreateFine();
  const updateFine = useUpdateFine();
  const deleteFine = useDeleteFine();
  const exportFines = useExportFines();
  const payFine = usePayFine();
  const { data: membersData } = useMembers();
  const isAdmin = useIsAdmin();

  // Calculate statistics from fines data
  const statistics = finesData?.data ? {
    summary: {
      total_fines: finesData.data.length,
      total_amount: finesData.data.reduce((sum, fine) => sum + (Number(fine.amount) || 0), 0),
    },
    by_status: {
      unpaid: {
        count: finesData.data.filter(fine => fine.status === 'unpaid').length,
        total_amount: finesData.data.filter(fine => fine.status === 'unpaid').reduce((sum, fine) => sum + (Number(fine.amount) || 0), 0),
      },
      paid: {
        count: finesData.data.filter(fine => fine.status === 'paid').length,
        total_amount: finesData.data.filter(fine => fine.status === 'paid').reduce((sum, fine) => sum + (Number(fine.amount) || 0), 0),
      },
      waived: {
        count: finesData.data.filter(fine => fine.status === 'waived').length,
        total_amount: finesData.data.filter(fine => fine.status === 'waived').reduce((sum, fine) => sum + (Number(fine.amount) || 0), 0),
      },
    },
    top_members: finesData.data.reduce((acc, fine) => {
      const memberId = fine.member?.id;
      if (memberId) {
        const existing = acc.find(item => item.member_id === memberId);
        const amount = Number(fine.amount) || 0;
        if (existing) {
          existing.fine_count += 1;
          existing.total_amount += amount;
        } else {
          acc.push({
            member_id: memberId,
            member_name: fine.member?.name || 'Unknown',
            fine_count: 1,
            total_amount: amount,
          });
        }
      }
      return acc;
    }, []).sort((a, b) => b.total_amount - a.total_amount).slice(0, 6),
  } : null;

  const formatCurrency = (amount) => {
    return `Ksh ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const columns = [
    { label: 'Member', field: 'member', sortable: true, render: row => row.member?.name || '-' },
    { label: 'Reason', field: 'reason', sortable: true },
    { label: 'Amount', field: 'amount', sortable: true, render: row => `Ksh ${row.amount}` },
    { label: 'Balance' , field: 'fine_balance', sortable: true, render: row =>  `Ksh ${row.fine_balance}`},
    { label: 'Status', field: 'status', sortable: true },
    { label: 'Issued At', field: 'created_at', sortable: true, render: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-' },
    { label: 'Paid At', field: 'paid_at', sortable: true, render: row => row.paid_at ? new Date(row.paid_at).toLocaleDateString() : '-' },
    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex space-x-2">
          {can(PERMISSIONS.EDIT_FINE) && (
          <button className="text-blue-600 hover:text-blue-800" onClick={() => { setSelectedFine(row); setIsModalOpen(true); }}><Pencil size={18} /></button>
          )}
              {row.status !== 'paid' && can(PERMISSIONS.EDIT_FINE) && (
                <button
                  className="text-green-600 hover:text-green-800"
                  onClick={() => {
                    setSelectedFine(row);
                    setShowFinePaymentModal(true);
                  }}
                >
                  <DollarSign size={18} />
                </button>
              )}
          {can(PERMISSIONS.DELETE_FINE) && (
          <button className="text-red-600 hover:text-red-800" onClick={() => handleDeleteFine(row.id)}><Trash2 size={18} /></button>
          )}
        </div>
      )
    },
  ];

  const handleCreateOrUpdateFine = (fine) => {
    if (selectedFine) {
      updateFine.mutate({ id: selectedFine.id, ...fine }, { onSuccess: () => { setIsModalOpen(false); setSelectedFine(null); } });
    } else {
      createFine.mutate(fine, { onSuccess: () => { setIsModalOpen(false); } });
    }
  };

  const handleDeleteFine = (id) => {
    if (window.confirm('Are you sure you want to delete this fine?')) {
      deleteFine.mutate(id);
    }
  };

  const handleFinePayment = (paymentData) => {
    payFine.mutate(paymentData, {
      
      onSuccess: () => {
        toast.success(`Fine payment made successfully`);
        setShowFinePaymentModal(false);
        setSelectedFine(null)
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to pay fine');
      }
    
    })
  }

  const handleExport = (format) => {
    exportFines.mutate({
      search: debouncedSearch,
      format,
    }, {
      onSuccess: () => {
        toast.success(`Fines exported successfully in ${format.toUpperCase()} format!`);
        setShowExportModal(false);
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to export fines');
      }
    });
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_FINES}>
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h2 className="text-2xl font-bold">Fines</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <PermissionGate permission={PERMISSIONS.CREATE_FINE}>
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto" onClick={() => { setSelectedFine(null); setIsModalOpen(true); }}>
              <Plus size={16} className="mr-2" /> Add Fine
            </button>
          </PermissionGate>
          {isAdmin && (
            <button 
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 w-full sm:w-auto"
              onClick={() => setShowExportModal(true)}
            >
              <Download size={16} className="mr-2" />
              Export
            </button>
          )}
        </div>
      </div>

      {/* Statistics Section */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="flex items-center mb-4">
          <BarChart3 size={20} className="mr-2 text-gray-600" />
          <h3 className="text-lg font-semibold">Fines Statistics</h3>
        </div>
        {isLoading ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded text-blue-700">Loading statistics...</div>
        ) : statistics ? (
          <>
            {/* Main Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 kpi-grid">
              {/* Total Fines */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <FileText size={24} className="text-blue-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Total Fines</p>
                <p className="text-2xl font-bold text-blue-800">{statistics.summary.total_fines}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.summary.total_amount)} total value</p>
              </div>

              {/* Paid Fines */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle size={24} className="text-green-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Paid Fines</p>
                <p className="text-2xl font-bold text-green-800">{statistics.by_status.paid.count}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.by_status.paid.total_amount)} value</p>
              </div>

              {/* Unpaid Fines */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Clock size={24} className="text-yellow-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Unpaid Fines</p>
                <p className="text-2xl font-bold text-yellow-800">{statistics.by_status.unpaid.count}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.by_status.unpaid.total_amount)} value</p>
              </div>

              {/* Waived Fines */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <XCircle size={24} className="text-red-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Waived Fines</p>
                <p className="text-2xl font-bold text-red-800">{statistics.by_status.waived.count}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.by_status.waived.total_amount)} value</p>
              </div>
            </div>

            {/* Top Members with Fines */}
            {statistics.top_members && statistics.top_members.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Award size={18} className="text-gray-600 mr-2" />
                  <h4 className="text-md font-semibold text-gray-800">Top Members with Fines</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {statistics.top_members.map((member, index) => (
                    <div key={member.member_id} className="bg-white rounded-lg p-3 border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-2">
                            <span className="text-xs font-bold text-red-600">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate">{member.member_name}</p>
                            <p className="text-xs text-gray-500">{member.fine_count} fines</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">{formatCurrency(member.total_amount)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-center">
            No statistics available.
          </div>
        )}
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search (reason, amount, etc)</label>
            <div className="relative">
              <input 
                type="text"
                placeholder="Search by reason, amount, etc..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      <Table columns={columns} data={finesData?.data || []} loading={isLoading} />
      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedFine(null); }}>
        <FineForm
          initialData={selectedFine}
          onSubmit={handleCreateOrUpdateFine}
          onCancel={() => { setIsModalOpen(false); setSelectedFine(null); }}
          members={membersData?.data || []}
        />
      </Modal>
      {/* Payment fine modal for admin */}
       {showFinePaymentModal && (
        <Modal
          isOpen={showFinePaymentModal}
          onClose={() => { 
            setShowFinePaymentModal(false); 
            setSelectedFine(null); 
          }}
        >
          <FinePaymentForm
            fineId={selectedFine?.id}
            onSubmit={handleFinePayment}
            onCancel={() => { 
              setShowFinePaymentModal(false); 
              setSelectedFine(null); 
            }}
          />
        </Modal>
      )}
      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Export Format</h2>
            <p className="text-gray-600 mb-6">Choose the format for your fines export:</p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-green-600 font-bold text-lg">X</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Excel (.xlsx)</div>
                    <div className="text-sm text-gray-500">Best for data analysis and calculations</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">CSV</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">CSV (.csv)</div>
                    <div className="text-sm text-gray-500">Simple text format, compatible with most applications</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-red-600 font-bold text-sm">PDF</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">PDF (.pdf)</div>
                    <div className="text-sm text-gray-500">Professional report format, perfect for sharing</div>
                  </div>
                </div>
              </button>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PermissionPage>
  );
}

function FineForm({ initialData, onSubmit, onCancel, members }) {
  const [memberId, setMemberId] = useState(initialData?.member_id || '');
  const [reason, setReason] = useState(initialData?.reason || '');
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [status, setStatus] = useState(initialData?.status || 'unpaid');
  const [paidAt, setPaidAt] = useState(initialData?.paid_at || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ member_id: memberId, reason, amount, status, paid_at: status === 'paid' ? paidAt : null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Member</label>
        <Select
          options={members.map(m => ({ value: m.id, label: m.name }))}
          value={members.map(m => ({ value: m.id, label: m.name })).find(option => option.value === parseInt(memberId))}
          onChange={(selectedOption) => setMemberId(selectedOption?.value?.toString() || '')}
          isClearable
          placeholder="Select member..."
          classNamePrefix="select"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Reason</label>
        <input type="text" className="w-full border rounded px-3 py-2" value={reason} onChange={e => setReason(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Amount</label>
        <input type="number" className="w-full border rounded px-3 py-2" value={amount} onChange={e => setAmount(e.target.value)} required min={0} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select className="w-full border rounded px-3 py-2" value={status} onChange={e => setStatus(e.target.value)} required>
          {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>
      {status === 'paid' && (
        <div>
          <label className="block text-sm font-medium mb-1">Paid At</label>
          <input type="date" className="w-full border rounded px-3 py-2" value={paidAt ? paidAt.substring(0, 10) : ''} onChange={e => setPaidAt(e.target.value)} />
        </div>
      )}
      <div className="flex justify-end space-x-2 pt-2">
        <button type="button" className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500" onClick={onCancel}>Cancel</button>
        <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">{initialData ? 'Update' : 'Add'} Fine</button>
      </div>
    </form>
  );
} 
function FinePaymentForm({ fineId, initialData, onSubmit, onCancel }) {
  const [description, setDescription] = useState(initialData?.description || '');
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [paymentDate, setPaymentDate] = useState(initialData?.payment_date || '');
  const [paymentMethod, setPaymentMethod] = useState(initialData?.payment_method || '');
  const [transactionReference, setTransactionReference] = useState(initialData?.transaction_reference || '');

  const handleSubmit = (e) => {
    e.preventDefault();

    onSubmit({
      fine_id: fineId,
      amount: Number(amount),
      payment_method: paymentMethod,
      payment_date: paymentDate,
      description,
      transaction_reference: transactionReference,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      <div>
        <label className="block text-sm font-medium mb-1">Amount</label>
        <input
          type="number"
          className="w-full border rounded px-3 py-2"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          required
          min={0}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Payment Method</label>
        <select
          className="w-full border rounded px-3 py-2"
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          required
        >
          <option value="">Select Method</option>
          <option value="cash">Cash</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="mobile_money">Mobile Money</option>
          <option value="digital_wallet">Digital Wallet</option>
          <option value="cheque">Cheque</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Payment Date</label>
        <input
          type="date"
          className="w-full border rounded px-3 py-2"
          value={paymentDate}
          onChange={e => setPaymentDate(e.target.value)}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <input
          type="text"
          className="w-full border rounded px-3 py-2"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Transaction Reference</label>
        <input
          type="text"
          className="w-full border rounded px-3 py-2"
          value={transactionReference}
          onChange={e => setTransactionReference(e.target.value)}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button type="button" className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">
          Pay Fine
        </button>
      </div>
    </form>
  );
}
